import { useMemo } from 'react'
import { useGetInsightsDataQuery } from '../../store/insights/insights'
import { INSIGHTS_CACHE_TTL_SECONDS } from './insightsUtils'
import type {
  InsightsDashboardData,
  InsightsDataCharts,
  Highlights,
  DiscussionTopicRow,
  InvestmentProductRow,
  ClientOutlook,
  AssetOpportunities,
  Referrals,
  TopicStatRow,
  InvestmentProductStatRow,
} from '../../store/insights/insights'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'

export type TimeRangeKey = '7D' | '30D' | '3M' | '6M' | '1Y'

export const TIME_RANGE_TO_DAYS: Record<TimeRangeKey, number> = {
  '7D': 7,
  '30D': 30,
  '3M': 90,
  '6M': 180,
  '1Y': 365,
}

export const TIME_RANGE_LABELS: Record<TimeRangeKey, string> = {
  '7D': '7 days',
  '30D': '30 days',
  '3M': '3 months',
  '6M': '6 months',
  '1Y': '1 year',
}

function toISODate(date: Date): string {
  return date.toISOString().split('T')[0]
}

function getDateRange(timeRange: TimeRangeKey): { from: string; to: string } {
  const to = new Date()
  const from = new Date(to)
  from.setDate(from.getDate() - TIME_RANGE_TO_DAYS[timeRange])
  return { from: toISODate(from), to: toISODate(to) }
}

function trendDirection(val: number): 'up' | 'down' | 'neutral' {
  if (val > 0) return 'up'
  if (val < 0) return 'down'
  return 'neutral'
}

// ── Highlights ──

function transformHighlights(charts: InsightsDataCharts): Highlights {
  const topicRows = charts.topic_stats ?? []
  // Coerce all numeric fields — API may return strings
  const totalMeetings = topicRows.length > 0 ? Math.round(Number(topicRows[0].total_meetings_current) || 0) : 0

  // Most discussed: topic with highest meetings_with_topic_current
  const sortedByMentions = [...topicRows].sort((a, b) => (Number(b.meetings_with_topic_current) || 0) - (Number(a.meetings_with_topic_current) || 0))
  const topMentioned = sortedByMentions[0]
  const mostDiscussed =
    topMentioned && String(topMentioned.topic ?? '').trim()
      ? {
          topic: String(topMentioned.topic).trim(),
          detail: (
            <>
              Came up in <strong>{Math.round(Number(topMentioned.meetings_with_topic_current) || 0)}</strong> of <strong>{totalMeetings}</strong> conversations
            </>
          ),
        }
      : null

  // Most client-driven: topic with highest client_driven_pct_current
  const sortedByClientDriven = [...topicRows].sort((a, b) => (Number(b.client_driven_pct_current) || 0) - (Number(a.client_driven_pct_current) || 0))
  const topClientDriven = sortedByClientDriven[0]
  const mostClientDriven =
    topClientDriven && String(topClientDriven.topic ?? '').trim()
      ? {
          topic: String(topClientDriven.topic).trim(),
          detail: (
            <>
              Clients raised this <strong>{Math.round(Number(topClientDriven.client_driven_meetings_current) || 0)}</strong> times
            </>
          ),
        }
      : null

  // Most trending: topic with highest mention increase in total_mentions
  const withTrend = topicRows
    .filter((r) => (Number(r.total_mentions_previous) || 0) > 0)
    .map((r) => {
      const curr = Number(r.total_mentions_current) || 0
      const prev = Number(r.total_mentions_previous) || 0
      const diff = curr - prev
      return { ...r, diff }
    })
    .sort((a, b) => b.diff - a.diff)
  const topTrending = withTrend[0]
  const mostTrending =
    topTrending && String(topTrending.topic ?? '').trim()
      ? {
          topic: String(topTrending.topic).trim(),
          detail: (
            <>
              <strong>{topTrending.diff}</strong> more mentions than last period
            </>
          ),
        }
      : null

  // Most questions raised: topic with highest questions_current
  const sortedByQuestions = [...topicRows].sort((a, b) => (Number(b.questions_current) || 0) - (Number(a.questions_current) || 0))
  const topQuestions = sortedByQuestions[0]
  const mostQuestionsRaised =
    topQuestions && String(topQuestions.topic ?? '').trim()
      ? {
          topic: String(topQuestions.topic).trim(),
          detail: (
            <>
              Clients asked <strong>{Math.round(Number(topQuestions.questions_current) || 0)}</strong> times about this topic
            </>
          ),
        }
      : null

  // Market outlook highlight
  const outlookData = charts.client_outlook
  let marketOutlook: Highlights['marketOutlook'] = null
  if (outlookData?.length) {
    let totalBullish = 0
    let totalNeutral = 0
    let totalBearish = 0
    let totalCount = 0
    let bestOpt = { asset: '', pct: 0 }
    let worstCon = { asset: '', pct: 0 }
    for (const row of outlookData) {
      totalBullish += Number(row.bullish_count) || 0
      totalNeutral += Number(row.neutral_count) || 0
      totalBearish += Number(row.bearish_count) || 0
      totalCount += Number(row.total_count) || 0
      const opt = Number(row.optimistic) || 0
      const con = Number(row.concerned) || 0
      if (opt > bestOpt.pct) bestOpt = { asset: row.asset_class, pct: opt }
      if (con > worstCon.pct) worstCon = { asset: row.asset_class, pct: con }
    }
    if (totalCount > 0) {
      const pctOpt = Math.round((totalBullish / totalCount) * 100)
      const pctNeu = Math.round((totalNeutral / totalCount) * 100)
      const pctCon = Math.round((totalBearish / totalCount) * 100)
      let summaryText: string
      if (pctNeu >= pctOpt && pctNeu >= pctCon) {
        summaryText = 'No strong market sentiment this period'
      } else if (pctCon > pctOpt) {
        summaryText = `${pctCon}% of clients feel cautious overall`
      } else {
        summaryText = `${pctOpt}% of clients feel optimistic overall`
      }
      marketOutlook = {
        optimisticPercent: pctOpt,
        neutralPercent: pctNeu,
        concernedPercent: pctCon,
        summaryText,
        strongestOpportunity: bestOpt.asset,
        topConcern: worstCon.asset,
      }
    }
  }

  // Held-away assets highlight
  const heldAwayData = charts.held_away_assets
  let heldAwayAssets: Highlights['heldAwayAssets'] = null
  if (heldAwayData?.length) {
    let totalMentioned = 0
    let totalSuggested = 0
    let totalAgreed = 0
    let leadMentions = { type: '', count: 0 }
    let bestConvert = { type: '', rate: 0 }
    for (const row of heldAwayData) {
      const mentioned = Math.round(Number(row.asset_mentioned_current) || 0)
      const suggested = Math.round(Number(row.suggestion_made_current) || 0)
      const agreed = Math.round(Number(row.client_agreed_current) || 0)
      totalMentioned += mentioned
      totalSuggested += suggested
      totalAgreed += agreed
      if (mentioned > leadMentions.count) leadMentions = { type: row.template_name, count: mentioned }
      const convertRate = suggested > 0 ? agreed / suggested : 0
      if (convertRate > bestConvert.rate) bestConvert = { type: row.template_name, rate: convertRate }
    }
    if (totalMentioned > 0 || totalSuggested > 0) {
      const suggestedPct = totalMentioned > 0 ? Math.round((totalSuggested / totalMentioned) * 100) : 0
      const agreedPct = totalSuggested > 0 ? Math.round((totalAgreed / totalSuggested) * 100) : 0
      heldAwayAssets = {
        suggestedPercent: suggestedPct,
        agreedPercent: agreedPct,
        summaryText: `${agreedPct}% of suggestions led to client agreement`,
        leadMeetingType: leadMentions.type || 'N/A',
        bestConvertMeetingType: bestConvert.type || 'N/A',
      }
    }
  }

  // Referrals highlight
  const referralData = charts.referrals
  let referralsHighlight: Highlights['referrals'] = null
  if (referralData?.length) {
    let totalAsked = 0
    let totalProvided = 0
    let leadAsks = { type: '', count: 0 }
    let leadProvided = { type: '', count: 0 }
    for (const row of referralData) {
      const asked = Math.round(Number(row.referrals_asked_current) || 0)
      const provided = Math.round(Number(row.referrals_provided_current) || 0)
      totalAsked += asked
      totalProvided += provided
      if (asked > leadAsks.count) leadAsks = { type: row.meeting_type, count: asked }
      if (provided > leadProvided.count) leadProvided = { type: row.meeting_type, count: provided }
    }
    const commitRate = totalAsked > 0 ? Math.round((totalProvided / totalAsked) * 100) : 0

    referralsHighlight = {
      commitRate,
      summaryText: totalAsked > 0 ? `${commitRate}% of referral asks led to a referral` : 'No referral activity yet',
      leadAskMeetingType: leadAsks.type || 'N/A',
      leadProvidedMeetingType: leadProvided.type || 'N/A',
    }
  }

  return {
    meetingCount: totalMeetings,
    mostDiscussed,
    mostClientDriven,
    mostTrending,
    mostQuestionsRaised,
    marketOutlook,
    heldAwayAssets,
    referrals: referralsHighlight,
  }
}

// ── Discussion Topics ──

function transformDiscussionTopics(rows: TopicStatRow[]): DiscussionTopicRow[] {
  return rows
    .filter((row) => String(row.topic ?? '').trim())
    .map((row) => {
      const curr = Number(row.total_mentions_current) || 0
      const prev = Number(row.total_mentions_previous) || 0
      const mentionChange = prev > 0 ? curr - prev : null
      return {
        topic: String(row.topic).trim(),
        mentions: curr,
        clientDrivenPercent: Math.round(Number(row.client_driven_pct_current) || 0),
        trendValue: mentionChange,
        trendDirection: mentionChange != null ? trendDirection(mentionChange) : null,
        questionsRaised: Math.round(Number(row.questions_current) || 0),
      }
    })
    .sort((a, b) => b.mentions - a.mentions)
}

// ── Investment Products ──

function transformInvestmentProducts(rows: InvestmentProductStatRow[]): InvestmentProductRow[] {
  return rows
    .filter((row) => String(row.sub_topic ?? '').trim())
    .map((row) => {
      const curr = Number(row.total_mentions_current) || 0
      const prev = Number(row.total_mentions_previous) || 0
      const mentionChange = prev > 0 ? curr - prev : null
      return {
        topic: String(row.sub_topic).trim(),
        subTopic: String(row.sub_topic).trim(),
        mentions: curr,
        clientDrivenPercent: Math.round(Number(row.client_driven_pct_current) || 0),
        trendValue: mentionChange,
        trendDirection: mentionChange != null ? trendDirection(mentionChange) : null,
        questionsRaised: Math.round(Number(row.questions_current) || 0),
      }
    })
    .sort((a, b) => b.mentions - a.mentions)
}

// ── Client Outlook ──

function transformClientOutlook(raw: InsightsDataCharts['client_outlook']): ClientOutlook | null {
  if (!raw?.length) return null

  const maxOptimistic = Math.max(...raw.map((r) => Number(r.optimistic) || 0))
  const maxConcerned = Math.max(...raw.map((r) => Number(r.concerned) || 0))

  const rows = raw.map((row) => {
    const opt = Number(row.optimistic) || 0
    const con = Number(row.concerned) || 0
    return {
      assetClass: row.asset_class ?? '',
      optimisticPercent: Math.round(opt),
      optimisticBarWidth: maxOptimistic > 0 ? Math.round((opt / maxOptimistic) * 100) : 0,
      concernedPercent: Math.round(con),
      concernedBarWidth: maxConcerned > 0 ? Math.round((con / maxConcerned) * 100) : 0,
    }
  })

  const optimisticRows = [...rows].sort((a, b) => b.optimisticPercent - a.optimisticPercent)
  const concernedRows = [...rows].sort((a, b) => b.concernedPercent - a.concernedPercent)
  return {
    optimisticRows,
    concernedRows,
    totalAssetClasses: rows.length,
  }
}

// ── Asset Opportunities ──

function transformAssetOpportunities(heldAwayRaw: InsightsDataCharts['held_away_assets']): AssetOpportunities | null {
  if (!heldAwayRaw?.length) return null

  let totalMentioned = 0
  let totalMentionedTrend = 0
  let totalSuggested = 0
  let totalSuggestedTrend = 0
  let totalAgreed = 0
  let totalAgreedTrend = 0
  let leadMentions = { type: '', count: 0 }
  let leadSuggestions = { type: '', count: 0 }
  let leadAgreed = { type: '', count: 0 }

  const rows = heldAwayRaw.map((row) => {
    const mentioned = Math.round(Number(row.asset_mentioned_current) || 0)
    const mentionedTrend = Math.round(Number(row.asset_mentioned_trend) || 0)
    const suggested = Math.round(Number(row.suggestion_made_current) || 0)
    const suggestedTrend = Math.round(Number(row.suggestion_made_trend) || 0)
    const agreed = Math.round(Number(row.client_agreed_current) || 0)
    const agreedTrend = Math.round(Number(row.client_agreed_trend) || 0)
    totalMentioned += mentioned
    totalMentionedTrend += mentionedTrend
    totalSuggested += suggested
    totalSuggestedTrend += suggestedTrend
    totalAgreed += agreed
    totalAgreedTrend += agreedTrend
    if (mentioned > leadMentions.count) leadMentions = { type: row.template_name, count: mentioned }
    if (suggested > leadSuggestions.count) leadSuggestions = { type: row.template_name, count: suggested }
    if (agreed > leadAgreed.count) leadAgreed = { type: row.template_name, count: agreed }
    const sugPct = mentioned > 0 ? Math.round((suggested / mentioned) * 100) : 0
    const agrPct = suggested > 0 ? Math.round((agreed / suggested) * 100) : 0
    return {
      meetingType: row.template_name,
      assetMentions: mentioned,
      assetMentionsTrend: mentionedTrend,
      suggestionMade: suggested,
      suggestionMadePct: sugPct,
      suggestionMadeTrend: suggestedTrend,
      clientAgreed: agreed,
      clientAgreedPct: agrPct,
      clientAgreedTrend: agreedTrend,
    }
  })

  if (totalMentioned === 0 && totalSuggested === 0 && totalAgreed === 0) return null

  return {
    metrics: {
      assetMentions: totalMentioned,
      assetMentionsTrend: totalMentionedTrend,
      assetMentionsLeadMeetingType: leadMentions.type || 'N/A',
      suggestionsMade: totalSuggested,
      suggestionsMadeTrend: totalSuggestedTrend,
      suggestionsLeadMeetingType: leadSuggestions.type || 'N/A',
      clientAgreed: totalAgreed,
      clientAgreedTrend: totalAgreedTrend,
      clientAgreedLeadMeetingType: leadAgreed.type || 'N/A',
    },
    rows,
  }
}

// ── Referrals ──

function transformReferrals(referralRaw: InsightsDataCharts['referrals']): Referrals | null {
  if (!referralRaw?.length) return null

  let totalAsked = 0
  let totalAskedTrend = 0
  let totalProvided = 0
  let totalProvidedTrend = 0
  let leadAsks = { type: '', count: 0 }
  let leadProvided = { type: '', count: 0 }

  const rows = referralRaw.map((row) => {
    const asked = Math.round(Number(row.referrals_asked_current) || 0)
    const askedTrend = Math.round(Number(row.referrals_asked_trend) || 0)
    const provided = Math.round(Number(row.referrals_provided_current) || 0)
    const providedTrend = Math.round(Number(row.referrals_provided_trend) || 0)
    totalAsked += asked
    totalAskedTrend += askedTrend
    totalProvided += provided
    totalProvidedTrend += providedTrend
    if (asked > leadAsks.count) leadAsks = { type: row.meeting_type, count: asked }
    if (provided > leadProvided.count) leadProvided = { type: row.meeting_type, count: provided }
    return {
      meetingType: String(row.meeting_type ?? ''),
      meetings: Math.round(Number(row.meetings_current) || 0),
      referralsAsked: asked,
      referralsAskedTrend: askedTrend,
      referralsProvided: provided,
      referralsProvidedTrend: providedTrend,
    }
  })

  return {
    metrics: {
      referralsAsked: totalAsked,
      referralsAskedTrend: totalAskedTrend,
      referralsAskedLeadMeetingType: leadAsks.type || 'N/A',
      referralsProvided: totalProvided,
      referralsProvidedTrend: totalProvidedTrend,
      referralsProvidedLeadMeetingType: leadProvided.type || 'N/A',
    },
    rows,
    isPartialData: false,
  }
}

// ── Hook ──

function buildEmptyData(timeRange: TimeRangeKey): InsightsDashboardData {
  return {
    timeRange,
    highlights: {
      meetingCount: 0,
      mostDiscussed: null,
      mostClientDriven: null,
      mostTrending: null,
      mostQuestionsRaised: null,
      marketOutlook: null,
      heldAwayAssets: null,
      referrals: null,
    },
    discussionTopics: [],
    investmentProducts: [],
    clientOutlook: null,
    assetOpportunities: null,
    referrals: null,
  }
}

interface UseInsightsDataOptions {
  enabled?: boolean
  userIdList?: string[]
}

export const useInsightsData = (timeRange: TimeRangeKey, { enabled = true, userIdList }: UseInsightsDataOptions = {}) => {
  const accountId = useAppSelector(selectAccountId)

  const {
    data: apiData,
    isFetching,
    isLoading,
    isError,
  } = useGetInsightsDataQuery(
    { accountId, ...getDateRange(timeRange), userIdList: userIdList?.length ? userIdList : undefined },
    { skip: !enabled, refetchOnMountOrArgChange: INSIGHTS_CACHE_TTL_SECONDS },
  )

  const data = useMemo((): InsightsDashboardData | null => {
    if (!apiData) {
      return null
    }

    const charts = apiData.charts ?? {}

    const highlights = transformHighlights(charts)
    const discussionTopics = transformDiscussionTopics(charts.topic_stats ?? [])
    const investmentProducts = transformInvestmentProducts(charts.investment_product_stats ?? [])
    const clientOutlook = transformClientOutlook(charts.client_outlook)
    const assetOpportunities = transformAssetOpportunities(charts.held_away_assets)
    const referrals = transformReferrals(charts.referrals)

    return {
      timeRange,
      highlights,
      discussionTopics,
      investmentProducts,
      clientOutlook,
      assetOpportunities,
      referrals,
    }
  }, [apiData, timeRange])

  const displayData = data ?? (enabled && !isError ? buildEmptyData(timeRange) : null)

  return {
    data: isLoading ? null : displayData,
    isLoading,
    isFetching,
    isError,
  }
}
